import CardHeader from "components/CardHeader";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { RootState } from "store/store";
import { Button, Card, CardBody } from "reactstrap";
import DateDisplay from "../../components/Date";
import { InvoiceState } from "../../store/reducers/Invoices";
import { getOrphanedPayments, linkOrphanedPayment } from "../../store/actions/Billing";
import { Invoice } from "../../models/Invoice";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import "./OrphanedPaymentsCard.scss";

interface Props {
    invoiceState: InvoiceState;
    invoice?: Invoice;
    limit?: number;
    daysBack?: number;
    loadData?: boolean;
    collapsible?: boolean;
    embedded?: boolean;
}

interface Actions {
    getOrphanedPayments: typeof getOrphanedPayments;
    linkOrphanedPayment: typeof linkOrphanedPayment;
}

const OrphanedPaymentsCard: React.FC<Props & Actions> = ({ invoice, limit = 10, daysBack, loadData = true, collapsible = false, embedded = false, linkOrphanedPayment, getOrphanedPayments, invoiceState }) => {
    const { t, intlLocale } = useOfficeTranslation();
    const currency = new Intl.NumberFormat(intlLocale, {style: "currency", currency: "EUR"});
    const [showAllPayments, setShowAllPayments] = useState(false);

    useEffect(() => {
        setShowAllPayments(false);
    }, [invoice?.id]);

    useEffect(() => {
        if (loadData) getOrphanedPayments()
        // eslint-disable-next-line
    }, [loadData]);

    let payments = invoiceState.getOrphanedPayments()
    if (invoice) {
        const invoiceClosedAt = invoice.GetCreatedDate().getTime();
        payments = payments.filter(payment =>
            payment.tenant === invoice.tenant && new Date(payment.date).getTime() >= invoiceClosedAt
        );
        if (!showAllPayments) {
            const latestPaymentDate = invoice.GetDeadlineDate();
            latestPaymentDate.setDate(latestPaymentDate.getDate() + 30);
            latestPaymentDate.setHours(23, 59, 59, 999);
            payments = payments.filter(payment => new Date(payment.date).getTime() <= latestPaymentDate.getTime());
        }
    } else if (daysBack !== undefined) {
        const earliestPaymentDate = new Date();
        earliestPaymentDate.setDate(earliestPaymentDate.getDate() - daysBack);
        payments = payments.filter(payment => new Date(payment.date) >= earliestPaymentDate)
    }

    const paymentContent = <>
                {payments.length === 0 ?
                    <p className="text-muted mt-3 mb-0">{t("dashboard.orphanedPaymentsCard.noOrphanedPaymentsFound")}</p>
                    :
                    <div className="orphaned-payments-list">
                        {payments
                            .sort((a, b) => (a.date < b.date) ? 1 : (a.date > b.date) ? -1 : 0)
                            .slice(0, showAllPayments ? undefined : limit)
                            .map(payment =>
                                <details key={payment.id} className="orphaned-payment">
                                    <summary>
                                        <strong className="orphaned-payment-name">{payment.metadata.counter_name}</strong>
                                        <strong className="orphaned-payment-value">{currency.format(payment.amount)}</strong>
                                        <span className="orphaned-payment-caption text-muted"><DateDisplay date={payment.date} /> · {payment.metadata.description}</span>
                                        <i className="mdi mdi-chevron-down orphaned-payment-chevron" aria-hidden="true" />
                                    </summary>
                                    <div className="orphaned-payment-expanded">
                                        <dl>
                                            <div><dt>{t("common.iban")}</dt><dd>{payment.metadata.counter_iban}</dd></div>
                                            <div><dt>{t("common.description")}</dt><dd>{payment.metadata.description}</dd></div>
                                        </dl>
                                        {invoice && <Button type="button" color="primary" size="sm" className="orphaned-payment-link"
                                            onClick={() => linkOrphanedPayment(payment.id, invoice.id)}>{t("common.link")}</Button>}
                                    </div>
                                </details>
                            )}
                    </div>
                }
                {(invoice || payments.length > limit) &&
                    <Button
                        type="button"
                        color="link"
                        className="mt-3 orphaned-payments-toggle"
                        aria-pressed={showAllPayments}
                        onClick={() => setShowAllPayments(previous => !previous)}
                    >
                        {t(showAllPayments
                            ? (invoice ? "dashboard.orphanedPaymentsCard.showSuggested" : "common.showLess")
                            : "dashboard.orphanedPaymentsCard.showAll")}
                    </Button>
                }
    </>;
    const body = <>
            {invoice && collapsible ? <details>
                <summary className="invoice-orphaned-summary">
                    <i className="mdi mdi-bank-transfer" aria-hidden="true" />
                    <span>{t("dashboard.orphanedPaymentsCard.orphanedPayments")}</span>
                    <span className="badge bg-light text-muted">{payments.length}</span>
                    <i className="mdi mdi-chevron-down" aria-hidden="true" />
                </summary>
                <div className="mt-3">{paymentContent}</div>
            </details> : <>
                <CardHeader title={t("dashboard.orphanedPaymentsCard.orphanedPayments")} icon="mdi-bank-transfer" count={payments.length} />
                {paymentContent}
            </>}
    </>;
    return embedded ? <div className="invoice-orphaned-embedded">{body}</div> :
        <Card className={invoice ? "invoice-orphaned-payments" : "h-100 mb-0 dashboard-widget-card"}><CardBody>{body}</CardBody></Card>;
}

const mapStateToProps = (state: RootState) => {
    return {
        invoiceState: state.Invoices,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ getOrphanedPayments, linkOrphanedPayment }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(OrphanedPaymentsCard);
